import { Extension, mergeAttributes } from "@tiptap/core";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import { BubbleMenu, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Code,
  Quote,
  Minus,
  List,
  ListOrdered,
  Link as LinkIcon,
  Unlink,
  AlignLeft,
  AlignCenter,
  AlignJustify,
  AlignRight,
  RemoveFormatting
} from "lucide-react";
import { cn } from "@gettactic/helpers";
import { toRichTextHtml } from "@gettactic/helpers/src/workplace-requests/richText";
import React from "react";
import { LinkDialog } from "./LinkDialog";
import { RICH_TEXT_CONTENT_CLASS } from "./rich-text-content";

/**
 * Swaps what the two Enters do: plain Enter breaks the line, Shift+Enter starts
 * a new paragraph.
 *
 * The way round for prose that is mostly short, related lines — an address, a
 * phone number, a caveat — where the default puts a paragraph's worth of space
 * between every one of them and the writer has to remember a modifier to avoid it.
 *
 * Inside a list Enter is left alone: there it already means "next item", and
 * taking that away would leave no way to add one.
 */
const LineBreakOnEnter = Extension.create({
  name: "lineBreakOnEnter",
  // Above the defaults, whose Enter would otherwise claim the key first.
  priority: 1000,
  addKeyboardShortcuts() {
    return {
      Enter: () => {
        if (
          this.editor.isActive("listItem") ||
          this.editor.isActive("blockquote")
        ) {
          return false;
        }
        return this.editor.commands.setHardBreak();
      },
      "Shift-Enter": () => this.editor.commands.splitBlock()
    };
  }
});

/**
 * Shows the address as a native tooltip on hover, so a link can be told apart
 * from its neighbours without moving the cursor into it or leaving the page.
 */
const LinkWithTitle = Link.extend({
  renderHTML({ HTMLAttributes }) {
    return [
      "a",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        title: HTMLAttributes.href
      }),
      0
    ];
  }
});

/**
 * How much formatting the editor offers.
 *
 * `"basic"` exists for content that is rendered through a strict allow-list
 * (`p, strong, em, u, ul, ol, li, a, br`). Offering the full toolbar there would let
 * an admin style a heading or a quote and then watch it vanish on render, with
 * nothing to explain why. The extensions are turned off at the root rather than
 * just hidden from the toolbar, so pasted markup is normalized the same way.
 */
export type TextEditorFormatting = "full" | "basic";

type Props = {
  value: string;
  onChange: (s: string | null) => void;
  id?: string;
  disabled?: boolean;
  contentClassName?: string;
  /** Defaults to `"full"` — every existing caller keeps the whole toolbar. */
  formatting?: TextEditorFormatting;
  /**
   * Enter breaks the line and Shift+Enter starts a paragraph, instead of the
   * other way round. Off by default, so the editors that already existed keep
   * the keys their authors are used to. See {@link LineBreakOnEnter}.
   */
  enterInsertsLineBreak?: boolean;
};

type ToolbarButtonProps = {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  title?: string;
};

function ToolbarButton({
  onClick,
  isActive,
  disabled,
  children,
  title
}: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        "p-1.5 rounded hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed",
        isActive && "bg-neutral-200"
      )}
    >
      {children}
    </button>
  );
}

function ToolbarGroup({
  children,
  hidden = false
}: {
  children: React.ReactNode;
  hidden?: boolean;
}) {
  if (hidden) {
    return null;
  }
  return (
    <div className="flex items-center gap-0.5 border-r border-neutral-200 pr-1 mr-1 last:border-0 last:pr-0 last:mr-0">
      {children}
    </div>
  );
}

export function TextEditor({
  value,
  onChange,
  id,
  disabled = false,
  contentClassName,
  formatting = "full",
  enterInsertsLineBreak = false
}: Props) {
  const isBasic = formatting === "basic";
  const [isLinkDialogOpen, setIsLinkDialogOpen] = React.useState(false);
  const extensions = React.useMemo(() => {
    const link = LinkWithTitle.configure({ openOnClick: false });
    const keys = enterInsertsLineBreak ? [LineBreakOnEnter] : [];
    if (isBasic) {
      return [
        StarterKit.configure({
          heading: false,
          blockquote: false,
          codeBlock: false,
          code: false,
          strike: false,
          horizontalRule: false
        }),
        Underline,
        link,
        ...keys
      ];
    }
    return [
      StarterKit,
      Underline,
      link,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      ...keys
    ];
  }, [isBasic, enterInsertsLineBreak]);

  // A value stored as plain text is loaded as HTML with its newlines kept;
  // see {@link toRichTextHtml}. It is only emitted back once the user edits.
  const html = toRichTextHtml(value);

  const editor = useEditor({
    extensions,
    content: html,
    editable: !disabled,
    // Tiptap renders client-side only; without this it warns about SSR
    // hydration mismatches when the editor mounts during a server render.
    immediatelyRender: false,
    onUpdate({ editor }) {
      let content: string | null = editor.getHTML();
      const json = editor.getJSON().content;

      // If the editor is empty, we need to null out the content, otherwise empty <p></p> tags will be rendered
      if (
        Array.isArray(json) &&
        json.length === 1 &&
        !json[0].hasOwnProperty("content")
      ) {
        content = null;
      }
      onChange(content);
    }
  });

  React.useEffect(() => {
    if (editor && value !== undefined) {
      const currentContent = editor.getHTML();
      if (currentContent !== html) {
        editor.commands.setContent(html);
      }
    }
  }, [editor, value, html]);

  React.useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  if (!editor) {
    return null;
  }

  // The address on the link the cursor currently sits in, "" when it is not in
  // one. Drives both the dialog's starting value and the bar under the toolbar.
  const activeLinkUrl: string = editor.isActive("link")
    ? (editor.getAttributes("link").href ?? "")
    : "";

  const applyLink = (url: string) => {
    // The selection survived the dialog in the editor's own state, so focusing
    // is enough to put the mark back exactly where it was picked.
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    setIsLinkDialogOpen(false);
  };

  const removeLink = () => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    setIsLinkDialogOpen(false);
  };

  const iconSize = 16;

  return (
    <div className="border border-neutral-300 rounded-md overflow-hidden">
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-neutral-200 bg-neutral-50">
        <ToolbarGroup>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive("bold")}
            title="Bold"
          >
            <Bold size={iconSize} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive("italic")}
            title="Italic"
          >
            <Italic size={iconSize} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editor.isActive("underline")}
            title="Underline"
          >
            <UnderlineIcon size={iconSize} />
          </ToolbarButton>
          {isBasic ? null : (
            <>
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleStrike().run()}
                isActive={editor.isActive("strike")}
                title="Strikethrough"
              >
                <Strikethrough size={iconSize} />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleCode().run()}
                isActive={editor.isActive("code")}
                title="Code"
              >
                <Code size={iconSize} />
              </ToolbarButton>
            </>
          )}
          {/* Clears marks *and* block type, so pasted text that arrived as a
              list or a heading comes back to a plain paragraph too — "clear
              formatting" that left the shape behind would read as broken. */}
          <ToolbarButton
            onClick={() =>
              editor
                .chain()
                .focus()
                .unsetAllMarks()
                .clearNodes()
                .unsetLink()
                .run()
            }
            title="Clear formatting"
          >
            <RemoveFormatting size={iconSize} />
          </ToolbarButton>
        </ToolbarGroup>

        <ToolbarGroup>
          {isBasic ? null : (
            <>
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                isActive={editor.isActive("blockquote")}
                title="Blockquote"
              >
                <Quote size={iconSize} />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
                title="Horizontal rule"
              >
                <Minus size={iconSize} />
              </ToolbarButton>
            </>
          )}
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive("bulletList")}
            title="Bullet list"
          >
            <List size={iconSize} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive("orderedList")}
            title="Ordered list"
          >
            <ListOrdered size={iconSize} />
          </ToolbarButton>
        </ToolbarGroup>

        <ToolbarGroup>
          <ToolbarButton
            onClick={() => setIsLinkDialogOpen(true)}
            isActive={!!activeLinkUrl}
            title={activeLinkUrl ? "Edit link" : "Add link"}
          >
            <LinkIcon size={iconSize} />
          </ToolbarButton>
          <ToolbarButton
            onClick={removeLink}
            disabled={!activeLinkUrl}
            title="Remove link"
          >
            <Unlink size={iconSize} />
          </ToolbarButton>
        </ToolbarGroup>

        <ToolbarGroup hidden={isBasic}>
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
            isActive={editor.isActive({ textAlign: "left" })}
            title="Align left"
          >
            <AlignLeft size={iconSize} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
            isActive={editor.isActive({ textAlign: "center" })}
            title="Align center"
          >
            <AlignCenter size={iconSize} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign("justify").run()}
            isActive={editor.isActive({ textAlign: "justify" })}
            title="Align justify"
          >
            <AlignJustify size={iconSize} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
            isActive={editor.isActive({ textAlign: "right" })}
            title="Align right"
          >
            <AlignRight size={iconSize} />
          </ToolbarButton>
        </ToolbarGroup>
      </div>

      {/* What the link under the cursor actually points at. A link's text is
          rarely its address, so without this the only way to check one is to
          save and click it.

          Floated beside the link rather than docked under the toolbar: a bar
          that appears and disappears as the cursor moves in and out of links
          would push the text down and back on every keystroke near one. */}
      <BubbleMenu
        editor={editor}
        shouldShow={({ editor: e }) => e.isActive("link")}
        tippyOptions={{ placement: "bottom", maxWidth: 420 }}
      >
        <div className="flex max-w-[420px] items-center gap-2 rounded-lg border border-neutral-200 bg-white px-2.5 py-1.5 text-xs shadow-md">
          <LinkIcon size={13} className="shrink-0 text-[#0f2449]" />
          <a
            href={activeLinkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="min-w-0 flex-1 truncate text-[#0f2449] underline underline-offset-2 hover:opacity-80"
          >
            {activeLinkUrl}
          </a>
          <button
            type="button"
            onClick={() => setIsLinkDialogOpen(true)}
            className="shrink-0 text-neutral-600 hover:text-neutral-900"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={removeLink}
            className="shrink-0 text-neutral-600 hover:text-neutral-900"
          >
            Remove
          </button>
        </div>
      </BubbleMenu>

      <EditorContent
        id={id}
        editor={editor}
        className={cn(
          "max-w-none p-3 min-h-[150px] focus:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[120px]",
          // Structure only — no size or colour, so the editors that already
          // existed keep inheriting exactly the type they inherit today.
          RICH_TEXT_CONTENT_CLASS,
          contentClassName
        )}
      />

      <LinkDialog
        isOpen={isLinkDialogOpen}
        initialUrl={activeLinkUrl}
        onClose={() => setIsLinkDialogOpen(false)}
        onSubmit={applyLink}
        onRemove={activeLinkUrl ? removeLink : undefined}
      />
    </div>
  );
}
